/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import jwtDecode from "jwt-decode";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
} from "context";


// Constants
const AVATAR_SIZE = 40;
const AVATAR_BORDER_COLOR = "#776F64";
const AVATAR_BORDER_WIDTH = "2px";
const TOKEN_KEYS = ["access_token", "token"];
const STORAGE_KEYS = ["token", "access_token", "refresh_token", "user"];

// Helper function to get user initials
function getUserInitials(name) {
  if (!name) return "";
  const nameParts = name.trim().split(" ");
  if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
  return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
}

// Helper function to get token from localStorage
function getStoredToken() {
  return TOKEN_KEYS.reduce((token, key) => {
    return token || localStorage.getItem(key);
  }, null);
}

// Helper function to decode user data from token
function decodeUserDataFromToken(token) {
  try {
    const decoded = jwtDecode(token);
    return {
      full_name: decoded.full_name,
      email: decoded.email,
      profile_pic_path: decoded.profile_pic_path
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
}

// Helper function to get user data from localStorage fallback
function getUserDataFromStorage() {
  try {
    const userData = JSON.parse(localStorage.getItem("user"));
    return userData || null;
  } catch {
    return null;
  }
}

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [userData, setUserData] = useState(() => {
    const token = getStoredToken();
    if (token) {
      return decodeUserDataFromToken(token);
    }
    return getUserDataFromStorage();
  });
  const [loadingUser] = useState(false);
  const navigate = useNavigate();

  // Update user data when token changes
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      const decodedUserData = decodeUserDataFromToken(token);
      if (decodedUserData) {
        setUserData(decodedUserData);
      }
    }
  }, []);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleCloseMenu = () => setOpenMenu(false);
  const handleProfileMenuOpen = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);
  const handleLogout = () => {
    STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
    setUserData(null);
    navigate("/authentication/sign-in");
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })} alignItems="center">
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={2}>
              {/* User Profile Section */}
              <MDBox
                display="flex"
                alignItems="center"
                gap={1}
                mr={2}
                sx={{ cursor: "pointer" }}
                onClick={handleProfileMenuOpen}
              >
                {loadingUser ? (
                  <Skeleton variant="circular" width={AVATAR_SIZE} height={AVATAR_SIZE} />
                ) : userData?.profile_pic_path ? (
                  <MDBox
                    component="img"
                    src={userData.profile_pic_path}
                    alt={userData?.full_name || "User"}
                    width={`${AVATAR_SIZE}px`}
                    height={`${AVATAR_SIZE}px`}
                    borderRadius="50%"
                    sx={{ 
                      objectFit: "cover", 
                      border: `${AVATAR_BORDER_WIDTH} solid ${AVATAR_BORDER_COLOR}` 
                    }}
                  />
                ) : (
                  <MDAvatar
                    bgColor="info"
                    size="md"
                    sx={{ 
                      width: `${AVATAR_SIZE}px`, 
                      height: `${AVATAR_SIZE}px`, 
                      border: `${AVATAR_BORDER_WIDTH} solid ${AVATAR_BORDER_COLOR}`,
                      fontSize: "16px",
                      fontWeight: "bold"
                    }}
                  >
                    {getUserInitials(userData?.full_name)}
                  </MDAvatar>
                )}
                <MDBox display="flex" flexDirection="column" alignItems="flex-end">
                  {loadingUser ? (
                    <Skeleton variant="text" width={80} height={24} />
                  ) : (
                    <MDTypography variant="button" fontWeight="medium" sx={{ color: "#776F64" }}>
                      {userData?.full_name || "Unknown User"}
                    </MDTypography>
                  )}
                  {loadingUser ? (
                    <Skeleton variant="text" width={120} height={16} />
                  ) : (
                    <MDTypography variant="caption" color="text">
                      {userData?.email || ""}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
              {/* End User Profile Section */}
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={handleLogout} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon fontSize="small">logout</Icon>
                  Sign out
                </MenuItem>
              </Menu>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
